import { Show, For, createMemo } from 'solid-js'
import { A } from '@solidjs/router'
import { characterStore } from '/web/store'
import { getAssetUrl } from '/web/shared/util'
import {
  abbreviateNumber,
  getFanCount,
  getModelUrl,
  getRating,
} from '/web/shared/aifans/lib/model-util'

interface Props {
  categorySlug?: string
  currentId?: string
}

// Deterministic per-index corner badge (matches target design att-2).
const BADGES = [
  { label: 'HOT', icon: '🔥', bg: '#A93FE0' },
  { label: 'NEW', icon: '⚡', bg: '#3B6FE0' },
  { label: 'VIP', icon: '', bg: '#3FB84D' },
  { label: 'TOP', icon: '👑', bg: '#E0A83F' },
]

const SuggestedCreators = (props: Props) => {
  const chars = characterStore((s) => ({ list: s.characters.list }))

  const suggestions = createMemo(() =>
    chars.list
      .filter(
        (c) =>
          props.categorySlug != null &&
          c.categorySlug === props.categorySlug &&
          c._id !== props.currentId
      )
      .slice(0, 4)
  )

  return (
    <Show when={suggestions().length > 0}>
      <section class="mx-auto w-full px-4 py-8 md:px-14 md:py-12">
        <h2 class="mb-6 font-bebas text-[26px] tracking-[1px] text-white md:mb-8">
          Profiles You Might Be Interested In
        </h2>
        <div class="grid grid-cols-2 gap-[14px] sm:gap-[22px] lg:grid-cols-4">
          <For each={suggestions()}>
            {(character, i) => {
              const badge = BADGES[i() % BADGES.length]
              const profileUrl = getModelUrl(
                character.categorySlug,
                character.slug || character._id
              )
              const fans = abbreviateNumber(getFanCount(character._id))
              const rating = getRating(character._id)

              return (
                <A
                  href={profileUrl}
                  class="group relative block overflow-hidden rounded-[14px] transition-transform duration-[250ms] hover:-translate-y-1"
                >
                  {/* Portrait image */}
                  <div class="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={getAssetUrl(character.avatar || '', true, { width: 400 })}
                      alt={character.name}
                      class="h-full w-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.06]"
                      style={{ 'object-position': 'center 18%' }}
                      loading="lazy"
                    />
                    {/* Corner badge */}
                    <span
                      class="absolute left-3 top-3 inline-flex items-center gap-1 rounded-[7px] px-2 py-1 font-rajdhani text-[11px] font-bold uppercase leading-none tracking-[0.5px] text-white"
                      style={{ background: badge.bg }}
                    >
                      <Show when={badge.icon}>
                        <span>{badge.icon}</span>
                      </Show>
                      {badge.label}
                    </span>
                  </div>

                  {/* Footer strip */}
                  <div class="bg-[#171430] px-4 py-[13px]">
                    <Show when={character.realName}>
                      <div class="mb-1 font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-[#4A9DE8]">
                        {character.realName}
                      </div>
                    </Show>
                    <div class="font-bebas text-[18px] leading-[1.05] tracking-[0.5px] text-white">
                      {character.name}
                    </div>
                    <div class="mt-2 flex items-center gap-2 font-rajdhani text-[13px]">
                      <span class="font-bold text-white">
                        <span style={{ color: '#F5B301' }}>★</span> {rating}
                      </span>
                      <span class="text-[var(--cf-muted)]">{fans} fans</span>
                    </div>
                  </div>
                </A>
              )
            }}
          </For>
        </div>
      </section>
    </Show>
  )
}

export default SuggestedCreators
